import React, { useMemo } from "react";
import { Alert, Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import moment from "moment";
import TableLoader from "../Loaders/TableLoader";

function fmtDate(v) {
  if (v == null || v === "") return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MM-YYYY") : String(v);
}

function statusChipProps(status) {
  const s = String(status || "").trim();
  if (s.toLowerCase() === "completed") {
    return { label: s, sx: { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600, border: "1px solid #A5D6A7" } };
  }
  return { label: s || "—", sx: { fontWeight: 600 } };
}

/**
 * My Points — completed sessions facilitated (SessionDetails / SessionHostDetails).
 */
export default function FacilitatingSessionsDrilldown({ activityName, rows, loading, error }) {
  const sorted = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return [...rows];
  }, [rows]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 0.5,
        mb: 1,
        border: "1px solid #e9d5ff",
        borderRadius: 2,
        bgcolor: "#faf5ff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #ede9fe", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#4c1d95" }}>
          Completed sessions (facilitated)
        </Typography>
        <Typography variant="caption" sx={{ color: "#6d28d9" }}>
          {activityName}
        </Typography>
        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
          ({sorted.length} record{sorted.length === 1 ? "" : "s"})
        </Typography>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : null}
      <TableContainer sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Session name", "Targeted skill", "Duration", "Date", "Status"].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#5b21b6",
                    bgcolor: "#f3e8ff",
                    whiteSpace: "nowrap",
                    py: 1,
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ border: 0, py: 2 }}>
                  <TableLoader />
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: "#6b7280", fontStyle: "italic", py: 2 }}>
                  No completed sessions found for you as host in the selected year.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r, idx) => {
                const chip = statusChipProps(r.status ?? r.Status);
                return (
                  <TableRow key={`${r.sessionName || ""}-${idx}`} hover sx={{ "& td": { borderColor: "#f3e8ff", fontSize: "0.8125rem" } }}>
                    <TableCell sx={{ fontWeight: 600, color: "#111827", maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.sessionName ?? r.SessionName ?? "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.targetedSkill ?? r.TargetedSkill ?? "—"}
                    </TableCell>
                    <TableCell>{r.duration ?? r.Duration ?? "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "#4b5563" }}>{fmtDate(r.date ?? r.Date)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={chip.label} sx={chip.sx} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
